import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      <div className="container">
        <nav className="breadcrumb">
          <Link to="/">Home</Link> / Privacy Policy
        </nav>
        
        <div className="privacy-content">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: July 8, 2025</p>

          <div className="intro">
            <p>
              Welcome to Skylink ("we," "us," or "our"). Skylink provides voice‐messaging, SMS and IVR services 
              to facilitate communication between incarcerated individuals and their friends and family. This Privacy 
              Policy explains how we collect, use, disclose, and protect information when you visit our website 
              (the "Site") or use our services (the "Services").
            </p>
          </div>

          <section>
            <h2>1. Information We Collect</h2>
            
            <h3>1.1 Personal Information</h3>
            <p>When you sign up for our Services or contact us, we may collect:</p>
            <ul>
              <li><strong>Contact information:</strong> name, email address, postal address, telephone number</li>
              <li><strong>Account credentials:</strong> username, password</li>
              <li><strong>Consent records:</strong> date/time stamp and method of opt-in for SMS/voice messaging</li>
              <li><strong>Payment information:</strong> credit/debit card details or other billing data, processed securely by our payment provider</li>
            </ul>

            <h3>1.2 Communications Data</h3>
            <ul>
              <li><strong>Call recordings & transcripts:</strong> audio files and text transcripts of IVR interactions, stored so that inmates and their contacts can leave and retrieve messages</li>
              <li><strong>SMS logs:</strong> content and metadata (sender, recipient, timestamp) of text messages we send or receive on your behalf</li>
            </ul>

            <h3>1.3 Usage & Device Data</h3>
            <ul>
              <li><strong>Log files:</strong> IP address, browser type, operating system, referral URLs, pages viewed, date/time stamps</li>
              <li><strong>Cookies & tracking:</strong> small data files stored on your device to remember preferences and improve site performance; you can disable cookies in your browser, though some features may not function</li>
            </ul>
          </section>

          <section>
            <h2>2. How We Use Your Information</h2>
            <p>We use collected data to:</p>
            <ul>
              <li><strong>Provide and manage Services:</strong> set up accounts, route calls/SMS, record voice prompts, generate transcripts</li>
              <li><strong>Communicate:</strong> send account notices, support messages, security alerts</li>
              <li><strong>Billing & fraud prevention:</strong> process payments, detect and prevent unauthorized activity</li>
              <li><strong>Improve our offerings:</strong> analyze usage trends, optimize IVR flows and user experience</li>
              <li><strong>Legal compliance:</strong> fulfill lawful requests from authorities, enforce our Terms of Service</li>
            </ul>
          </section>

          <section>
            <h2>3. SMS & Voice Messaging Practices</h2>
            <ul>
              <li>We only send SMS or record calls after you have explicitly opted in (e.g., via our web form or IVR opt-in prompt).</li>
              <li><strong>Message Types:</strong> We may send transactional SMS messages, service notifications, security alerts, and account verification messages.</li>
                             <li><strong>Message Frequency:</strong> Message frequency will vary based on your account activity and service usage.</li>
              <li><strong>Message and Data Rates May Apply:</strong> Your carrier's standard messaging rates apply to SMS messages.</li>
              <li><strong>Opt-out Instructions:</strong> You may opt out of SMS at any time by replying <strong>STOP</strong> to any message, or revoke call consent by contacting us at <a href="mailto:connectinoutinc@gmail.com">connectinoutinc@gmail.com</a>.</li>
              <li><strong>HELP Message:</strong> Text <strong>HELP</strong> to any message to receive assistance.</li>
              <li><strong>Consent Not Required for Purchase:</strong> Your consent to receive SMS messages is not a condition of purchasing any goods or services.</li>
              <li><strong>No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. All other categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.</strong></li>
            </ul>
          </section>

          <section>
            <h2>4. Sharing & Disclosure</h2>
            <p>We may share your information with:</p>
            <ul>
              <li><strong>Service providers:</strong> payment processors, telephony carriers, cloud hosting vendors, analytics providers—only to the extent necessary to deliver Services</li>
              <li><strong>Legal authorities:</strong> to comply with valid subpoenas, court orders, or legal processes</li>
              <li><strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets (we will require the recipient to honor this Privacy Policy)</li>
            </ul>
            <p>We do <strong>not</strong> sell your personal data to marketers or other third parties.</p>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>
              We implement reasonable administrative, technical, and physical safeguards to protect your information. 
              Access to sensitive data (payments, transcripts) is restricted to authorized personnel. However, no 
              system is completely secure—please use strong, unique passwords.
            </p>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>We retain:</p>
            <ul>
              <li><strong>Account & billing data</strong> for as long as your account is active and to comply with financial recordkeeping laws</li>
              <li><strong>Call recordings & transcripts</strong> until you delete them or as required by facility policy</li>
              <li><strong>Log data</strong> for up to 12 months to diagnose issues and improve Services</li>
            </ul>
          </section>

          <section>
            <h2>7. Terms of Service</h2>
            <p>
              These privacy practices are part of our broader Terms of Service. Please review our{' '}
              <a href="/terms">Terms of Service</a> for complete information about your use of our services.
            </p>
          </section>

          <section>
            <h2>8. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul>
              <li>Access, correct, update, or delete your personal information</li>
              <li>Restrict or object to certain processing activities</li>
              <li>Receive a copy of your data in a portable format</li>
              <li>Withdraw consent at any time (without affecting prior lawfulness)</li>
            </ul>
            <p>To exercise these rights, please contact <a href="mailto:connectinoutinc@gmail.com">connectinoutinc@gmail.com</a>.</p>
          </section>

          <section>
            <h2>9. Children's Privacy</h2>
            <p>
              Our Services are <strong>not</strong> directed to children under 13. We do not knowingly collect 
              personal information from anyone under 13. If we learn we have done so, we will promptly delete that data.
            </p>
          </section>

          <section>
            <h2>10. Third-Party Links</h2>
            <p>
              Our Site may contain links to third-party websites. We are not responsible for their privacy practices. 
              Please review each site's policy before providing personal information.
            </p>
          </section>

          <section>
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will post the new effective date at the top 
              and, where required by law, notify you via email or on our Site before changes take effect.
            </p>
          </section>

          <section>
            <h2>12. Contact Us</h2>
            <p>If you have questions or concerns about this Privacy Policy, or wish to exercise your data rights, please contact us at:</p>
            
                         <div className="contact-info">
               <p><strong>Skylink, Inc.</strong></p>
               <p>Email: <a href="mailto:connectinoutinc@gmail.com">connectinoutinc@gmail.com</a></p>
               <p>Address: PO Box 35437, Boston, MA 02135</p>
             </div>
            
            <p>Thank you for trusting Skylink with your communication needs.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 